import { Pencil, Plus, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import Select from 'react-select'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddArea from '../../../components/modal/Area/ModalAddArea'
import ModalUpdateArea from '../../../components/modal/Area/ModalUpdateArea'

const MasterArea = () => {
  const [modalAddShow, setModalAddShow] = useState(false)
  const [modalUpdateShow, setModalUpdateShow] = useState(false)
  const [editId, setEditId] = useState(0)
  const [areas, setDataArea] = useState([])
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)
  const { per_page, current_page } = meta
  const [warehouses, setWarehouses] = useState([])

  const [filter, setFilter] = useState({
    warehouseId: null,
    search: ''
  })

  const prevWarehouse = useRef(filter.warehouseId)
  const abortControllerRef = useRef(null)

  const cardFilterStyle = {
    border: 'none',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    backgroundColor: '#fff'
  }

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: '#333333',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    letterSpacing: '0.5px'
  }

  const SelectStyles = {
    control: (base, state) => ({
      ...base,
      height: 45,
      minHeight: 45,
      borderRadius: '8px',
      borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
      backgroundColor: '#ffffff',
      border: '1px solid #e9ecef',
      fontSize: '0.9rem'
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '45px',
      padding: '0 12px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'var(--primary-orange)'
        : state.isFocused
          ? '#fff5e6'
          : null,
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer'
    })
  }

  const headerTitleStyle = {
    color: 'var(--primary-orange)',
    fontSize: '24px',
    fontWeight: '600'
  }

  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    color: '#333333',
    fontWeight: '700',
    borderBottom: '1px solid #e9ecef',
    textTransform: 'uppercase',
    fontSize: '0.85rem'
  }

  const loadArea = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const response = await api.get('/master/area', {
        signal,
        params: {
          per_page,
          page: current_page,
          warehouse_id: filter.warehouseId?.value || ''
        }
      })

      if (response.data.success) {
        const { metadata, data } = response.data
        setDataArea(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const formatted = data.map((d) => ({
        value: d.id,
        label: d.warehouse_name
      }))
      setWarehouses(formatted)
    } catch (err) {
      // ignore
    }
  }

  useEffect(() => {
    const warehouseChanged = prevWarehouse.current !== filter.warehouseId

    if (!warehouseChanged) return

    prevWarehouse.current = filter.warehouseId

    if (!filter.warehouseId) {
      setDataArea([])
      setMeta((prev) => ({
        ...prev,
        current_page: 1,
        total_row: 0,
        total_page: 0
      }))
      return
    }

    if (current_page !== 1) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    } else {
      loadArea()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.warehouseId])

  useEffect(() => {
    if (filter.warehouseId) {
      loadArea()
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page])

  useEffect(() => {
    fetchWarehouse()
  }, [])

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    setModalUpdateShow(true)
    setEditId(id)
  }

  const resetFilters = () => {
    setFilter({ warehouseId: null, search: '' })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  return (
    <>
      <div className="container-fluid px-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Area</h3>
          </Col>
          <Col className="text-end">
            <Button
              className="btn btn-primary-orange shadow-sm rounded px-4"
              onClick={() => setModalAddShow(true)}
              disabled={!filter.warehouseId}
            >
              <Plus size={16} className="me-2" />
              Add Area
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={10}>
              <Form.Group>
                <Form.Label style={labelStyle}>Warehouse</Form.Label>
                <Select
                  options={warehouses}
                  styles={SelectStyles}
                  value={filter.warehouseId}
                  onChange={(val) => setFilter({ ...filter, warehouseId: val })}
                  placeholder="Select Warehouse..."
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="light"
                className="w-100 rounded shadow-sm"
                style={{
                  height: '45px',
                  borderColor: '#e9ecef',
                  color: '#6c757d',
                  fontWeight: '600'
                }}
                onClick={resetFilters}
              >
                <RotateCw size={16} className="me-2" />
                Reset
              </Button>
            </Col>
          </Row>
        </Card>

        <Row className="mt-3">
          <Col md={12}>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <Table responsive hover className="mb-0 b-table-frameless">
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th className="p-2 align-middle" style={{ width: 150 }}>
                      Code
                    </th>
                    <th className="p-2 align-middle" style={{ width: 1000 }}>
                      Name
                    </th>
                    <th className="p-2 align-middle text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="text-center p-5">
                        <Loader />
                      </td>
                    </tr>
                  )}
                  {!loading && areas.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <RowEmpty colSpan={3} />
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    areas.length > 0 &&
                    areas.map((data) => {
                      return (
                        <tr key={data.id}>
                          <td className="p-2 align-middle text-dark fw-medium">
                            {data.code}
                          </td>
                          <td className="p-2 align-middle text-muted">
                            {data.name}
                          </td>
                          <td className="p-2 align-middle text-end">
                            <TableAction>
                              <button
                                className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => handleEdit(data.id)}
                              >
                                <Pencil size={13} />
                              </button>
                            </TableAction>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </Table>
              {areas.length > 0 && (
                <div className="p-3 border-top bg-white">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      <ModalAddArea
        show={modalAddShow}
        onHide={() => setModalAddShow(false)}
        refreshData={() => loadArea()}
        warehouseId={filter.warehouseId?.value}
      ></ModalAddArea>
      <ModalUpdateArea
        show={modalUpdateShow}
        onHide={() => setModalUpdateShow(false)}
        id={editId}
        refreshData={() => loadArea()}
      ></ModalUpdateArea>
    </>
  )
}

export default MasterArea
